import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@shared/api/client';
import type { UpdatePlatformConfigPayload } from '@shared/types/api';
import { useAdminConfig, useUpdateConfig } from '@/hooks/use-admin';
import { useSectionPermission } from '@/hooks/use-admin-permissions';

export function AdminConfigPage() {
  const { data: cfg, isLoading } = useAdminConfig();
  const update = useUpdateConfig();
  const { canEdit, isSuperAdmin } = useSectionPermission('CONFIG');
  const isReadOnly = !canEdit && !isSuperAdmin;
  const [form, setForm] = useState<UpdatePlatformConfigPayload>({});

  useEffect(() => {
    if (!cfg) return;
    setForm({
      platformFeePercentage: cfg.platformFeePercentage,
      platformFeeFlatPaise: cfg.platformFeeFlatPaise,
      paymentGatewayFeeNonRefundable: cfg.paymentGatewayFeeNonRefundable,
      supportPeriodDays: cfg.supportPeriodDays,
      refundProcessingDays: cfg.refundProcessingDays,
      invoiceAutoCancelDays: cfg.invoiceAutoCancelDays,
      ecregiLegalName: cfg.ecregiLegalName,
      ecregiGstin: cfg.ecregiGstin,
      ecregiPan: cfg.ecregiPan,
      ecregiState: cfg.ecregiState,
      ecregiRegisteredAddress: cfg.ecregiRegisteredAddress,
      defaultGstPercentage: cfg.defaultGstPercentage,
      defaultTdsPercentageForPayout: cfg.defaultTdsPercentageForPayout,
      tdsSectionCode: cfg.tdsSectionCode,
    });
  }, [cfg]);

  function set<K extends keyof UpdatePlatformConfigPayload>(
    k: K,
    v: UpdatePlatformConfigPayload[K],
  ) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  function submit() {
    update.mutate(form);
  }

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Platform config</h1>
          <p className="text-sm text-gray-600">
            Platform-wide fees and identity snapshotted onto every new invoice / payout.
          </p>
        </div>
        {isReadOnly ? (
          <span className="inline-flex items-center rounded-md bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
            View Only Mode
          </span>
        ) : null}
      </div>

      {isLoading || !cfg ? (
        <p className="mt-6 text-sm text-gray-500">Loading…</p>
      ) : (
        <fieldset disabled={isReadOnly} className="mt-6 space-y-5">
          <Section title="Fees">
            <Grid>
              <L label="Platform fee %">
                <Input autoFocus onFocus={(e) => e.target.select()} value={(form.platformFeePercentage as string) ?? ''} onChange={(e) => set('platformFeePercentage', e.target.value)} />
              </L>
              <L label="Platform fee flat (paise)">
                <Input onFocus={(e) => e.target.select()} value={(form.platformFeeFlatPaise as string) ?? ''} onChange={(e) => set('platformFeeFlatPaise', e.target.value.replace(/\D/g, ''))} />
              </L>
              <L label="Default GST %">
                <Input onFocus={(e) => e.target.select()} value={(form.defaultGstPercentage as string) ?? ''} onChange={(e) => set('defaultGstPercentage', e.target.value)} />
              </L>
              <L label="TDS % for payouts">
                <Input onFocus={(e) => e.target.select()} value={(form.defaultTdsPercentageForPayout as string) ?? ''} onChange={(e) => set('defaultTdsPercentageForPayout', e.target.value)} />
              </L>
              <L label="TDS section code">
                <Input onFocus={(e) => e.target.select()} value={form.tdsSectionCode ?? ''} onChange={(e) => set('tdsSectionCode', e.target.value)} />
              </L>
              <L label="PG fee non-refundable">
                <select
                  value={form.paymentGatewayFeeNonRefundable ? 'true' : 'false'}
                  onChange={(e) => set('paymentGatewayFeeNonRefundable', e.target.value === 'true')}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm"
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </L>
            </Grid>
          </Section>

          <Section title="Durations">
            <Grid>
              <L label="Support window (days)">
                <Input type="number" onFocus={(e) => e.target.select()} value={form.supportPeriodDays ?? ''} onChange={(e) => set('supportPeriodDays', Number(e.target.value))} />
              </L>
              <L label="Refund processing (days)">
                <Input type="number" onFocus={(e) => e.target.select()} value={form.refundProcessingDays ?? ''} onChange={(e) => set('refundProcessingDays', Number(e.target.value))} />
              </L>
              <L label="Invoice auto-cancel (days)">
                <Input type="number" onFocus={(e) => e.target.select()} value={form.invoiceAutoCancelDays ?? ''} onChange={(e) => set('invoiceAutoCancelDays', Number(e.target.value))} />
              </L>
            </Grid>
          </Section>

          <Section title="EcRegi identity (on every invoice)">
            <Grid>
              <L label="Legal name">
                <Input onFocus={(e) => e.target.select()} value={form.ecregiLegalName ?? ''} onChange={(e) => set('ecregiLegalName', e.target.value)} />
              </L>
              <L label="GSTIN">
                <Input onFocus={(e) => e.target.select()} value={form.ecregiGstin ?? ''} onChange={(e) => set('ecregiGstin', e.target.value.toUpperCase())} />
              </L>
              <L label="PAN">
                <Input onFocus={(e) => e.target.select()} value={form.ecregiPan ?? ''} onChange={(e) => set('ecregiPan', e.target.value.toUpperCase())} />
              </L>
              <L label="State">
                <Input onFocus={(e) => e.target.select()} value={form.ecregiState ?? ''} onChange={(e) => set('ecregiState', e.target.value)} />
              </L>
            </Grid>
            <L label="Registered address">
              <textarea
                value={form.ecregiRegisteredAddress ?? ''}
                onChange={(e) => set('ecregiRegisteredAddress', e.target.value)}
                onFocus={(e) => e.target.select()}
                rows={2}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              />
            </L>
          </Section>

          {update.isError ? <p className="text-sm text-red-600">{apiErrorMessage(update.error)}</p> : null}
          {update.isSuccess ? <p className="text-sm text-brand-700">Saved.</p> : null}

          {!isReadOnly && (
            <div className="flex justify-end">
              <Button onClick={submit} disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save config'}
              </Button>
            </div>
          )}
        </fieldset>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2">{children}</div>;
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-gray-700">{label}</div>
      {children}
    </label>
  );
}
